import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Send, Mic, MicOff, Bot, User, Sparkles, Clock, MessageSquare } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DemoInterview() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  
  const [isLoading, setIsLoading] = useState(false);
  const [demoSessionId, setDemoSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [questionsAsked, setQuestionsAsked] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [jobTitle, setJobTitle] = useState('Développeur Full Stack');
  const [jobDescription, setJobDescription] = useState('Nous recherchons un développeur Full Stack avec 3+ ans d\'expérience en React et Python.');
  const [hasStarted, setHasStarted] = useState(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const startDemo = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/interview/demo/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          job_title: jobTitle,
          job_description: jobDescription,
          language: 'fr'
        })
      });

      if (!response.ok) throw new Error('Failed to start demo');

      const data = await response.json();
      setDemoSessionId(data.demo_session_id);
      setHasStarted(true);
      setMessages([{
        role: 'assistant',
        content: data.message,
        type: data.question_type,
        timestamp: new Date().toISOString()
      }]);
    } catch (error) {
      console.error('Error starting demo:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const sendResponse = async () => {
    if (!inputText.trim() || isLoading || isComplete) return;

    const userMessage = inputText.trim();
    setInputText('');
    
    // Add user message to chat
    setMessages(prev => [...prev, {
      role: 'user',
      content: userMessage,
      timestamp: new Date().toISOString()
    }]);

    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/interview/demo/${demoSessionId}/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ response_text: userMessage })
      });

      if (!response.ok) throw new Error('Failed to send response');

      const data = await response.json();
      setQuestionsAsked(data.questions_asked);
      setIsComplete(data.interview_complete);
      
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.message,
        type: data.question_type,
        isFollowUp: data.is_follow_up,
        timestamp: new Date().toISOString()
      }]);
    } catch (error) {
      console.error('Error sending response:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const endDemo = async () => {
    if (!demoSessionId) return;
    
    try {
      await fetch(`${API_URL}/api/interview/demo/${demoSessionId}/end`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Error ending demo:', error);
    }
    navigate('/dashboard');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendResponse();
    }
  };

  // Setup screen (before starting)
  if (!hasStarted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-zinc-50 to-zinc-100">
        <div className="container mx-auto px-4 py-8 max-w-2xl">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/dashboard')}
            className="mb-6"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour au dashboard
          </Button>

          <Card className="border-0 shadow-xl">
            <CardHeader className="text-center pb-2">
              <div className="mx-auto w-16 h-16 rounded-full bg-lime-100 flex items-center justify-center mb-4">
                <Sparkles className="h-8 w-8 text-lime-600" />
              </div>
              <CardTitle className="text-2xl">Mode Démo</CardTitle>
              <CardDescription className="text-base">
                Testez l'entretien IA en jouant le rôle du candidat
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6 pt-4">
              <div className="bg-lime-50 border border-lime-200 rounded-lg p-4">
                <div className="flex items-center gap-2 text-lime-700 font-medium mb-2">
                  <Bot className="h-5 w-5" />
                  Comment ça marche ?
                </div>
                <ul className="text-sm text-lime-800 space-y-1 ml-7 list-disc">
                  <li>Ema vous posera des questions comme à un vrai candidat</li>
                  <li>Répondez comme si vous passiez l'entretien</li>
                  <li>Aucun crédit n'est consommé</li>
                  <li>Découvrez comment vos candidats vivront l'expérience</li>
                </ul>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-2">
                    Titre du poste (démo)
                  </label>
                  <Input
                    value={jobTitle}
                    onChange={(e) => setJobTitle(e.target.value)}
                    placeholder="Ex: Développeur Full Stack"
                    data-testid="demo-job-title"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-2">
                    Description courte
                  </label>
                  <Input
                    value={jobDescription}
                    onChange={(e) => setJobDescription(e.target.value)}
                    placeholder="Ex: 3+ ans d'expérience React/Python"
                    data-testid="demo-job-description"
                  />
                </div>
              </div>

              <Button 
                onClick={startDemo} 
                disabled={isLoading}
                className="w-full bg-zinc-900 hover:bg-zinc-800 h-12 text-base"
                data-testid="start-demo-btn"
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                    Démarrage...
                  </div>
                ) : (
                  <>
                    <Sparkles className="h-5 w-5 mr-2" />
                    Démarrer l'entretien démo
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Interview chat screen
  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-900 to-zinc-800 flex flex-col">
      {/* Header */}
      <div className="bg-zinc-900/80 backdrop-blur-lg border-b border-zinc-700 px-4 py-3">
        <div className="container mx-auto max-w-3xl flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-lime-500 flex items-center justify-center">
              <Bot className="h-5 w-5 text-zinc-900" />
            </div>
            <div>
              <h1 className="text-white font-semibold">Ema - Mode Démo</h1>
              <p className="text-zinc-400 text-sm">{jobTitle}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant="outline" className="border-lime-500 text-lime-500">
              <MessageSquare className="h-3 w-3 mr-1" />
              {questionsAsked}/15 questions
            </Badge>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={endDemo}
              className="border-zinc-600 text-zinc-300 hover:bg-zinc-800"
              data-testid="end-demo-btn"
            >
              Terminer
            </Button>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-6">
        <div className="container mx-auto max-w-3xl space-y-4">
          {messages.map((msg, idx) => (
            <div 
              key={idx}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div className={`flex items-start gap-3 max-w-[85%] ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  msg.role === 'user' 
                    ? 'bg-blue-500' 
                    : 'bg-lime-500'
                }`}>
                  {msg.role === 'user' 
                    ? <User className="h-4 w-4 text-white" />
                    : <Bot className="h-4 w-4 text-zinc-900" />
                  }
                </div>
                <div className={`rounded-2xl px-4 py-3 ${
                  msg.role === 'user'
                    ? 'bg-blue-500 text-white'
                    : 'bg-zinc-700 text-zinc-100'
                }`}>
                  {msg.type && msg.role === 'assistant' && (
                    <Badge className="mb-2 bg-zinc-600 text-zinc-300 text-xs">
                      {msg.type === 'intro' ? 'Introduction' :
                       msg.type === 'hard_skill' ? 'Compétences techniques' :
                       msg.type === 'soft_skill' ? 'Soft skills' :
                       msg.type === 'situational' ? 'Question situationnelle' :
                       msg.type === 'closing' ? 'Conclusion' : msg.type}
                    </Badge>
                  )}
                  <p className="whitespace-pre-wrap">{msg.content}</p>
                </div>
              </div>
            </div>
          ))}
          
          {isLoading && (
            <div className="flex justify-start">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-lime-500 flex items-center justify-center">
                  <Bot className="h-4 w-4 text-zinc-900" />
                </div>
                <div className="bg-zinc-700 rounded-2xl px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="animate-bounce h-2 w-2 bg-lime-400 rounded-full" style={{ animationDelay: '0ms' }} />
                    <div className="animate-bounce h-2 w-2 bg-lime-400 rounded-full" style={{ animationDelay: '150ms' }} />
                    <div className="animate-bounce h-2 w-2 bg-lime-400 rounded-full" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {isComplete && (
            <div className="text-center py-6">
              <div className="inline-flex items-center gap-2 bg-lime-500/20 text-lime-400 px-4 py-2 rounded-full">
                <Sparkles className="h-4 w-4" />
                Entretien démo terminé !
              </div>
              <p className="text-zinc-400 mt-2 text-sm">
                C'est ainsi que vos candidats vivront l'expérience avec Ema.
              </p>
              <Button 
                onClick={endDemo}
                className="mt-4 bg-lime-500 hover:bg-lime-600 text-zinc-900"
                data-testid="finish-demo-btn"
              >
                Retour au dashboard
              </Button>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input */}
      {!isComplete && (
        <div className="bg-zinc-900/80 backdrop-blur-lg border-t border-zinc-700 px-4 py-4">
          <div className="container mx-auto max-w-3xl">
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="icon"
                onClick={() => setIsRecording(!isRecording)}
                className={`border-zinc-600 ${isRecording ? 'bg-red-500 border-red-500 text-white' : 'text-zinc-300 hover:bg-zinc-800'}`}
                data-testid="mic-btn"
              >
                {isRecording ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
              </Button>
              <Input
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Tapez votre réponse..."
                className="flex-1 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                disabled={isLoading}
                data-testid="demo-input"
              />
              <Button
                onClick={sendResponse}
                disabled={isLoading || !inputText.trim()}
                className="bg-lime-500 hover:bg-lime-600 text-zinc-900"
                data-testid="send-response-btn"
              >
                <Send className="h-5 w-5" />
              </Button>
            </div>
            <p className="text-center text-zinc-500 text-xs mt-2">
              Mode démo • Aucun crédit consommé
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
